// Export coerente per-kind degli artefatti PA (Task #3817): mappa ogni tipo
// esportabile nelle sue opzioni di download, usata dall'header del pannello
// artefatti. I tipi media (file/image/video/audio) hanno già i loro bottoni
// nel viewer e il tipo `action` non ha nulla da esportare.

import { buildCsv, downloadBlob, exportFilename, exportTableXlsx, type ExportColumn, type ExportRow } from './tableExport';
import type { PaArtifact } from '@easyflow/shared';

export interface ArtifactExportOption {
  id: string;
  label: string;
  run: () => Promise<void>;
}

interface BodyContent { body?: unknown }
interface TableLikeContent { columns?: unknown; rows?: unknown; data?: unknown; xKey?: unknown; yKey?: unknown }

function bodyOf(artifact: PaArtifact): string {
  const c = artifact.content as BodyContent | null | undefined;
  return typeof c?.body === 'string' ? c.body : '';
}

const CODE_EXT: Record<string, string> = {
  typescript: 'ts', ts: 'ts', tsx: 'tsx', javascript: 'js', js: 'js', jsx: 'jsx',
  python: 'py', sql: 'sql', json: 'json', yaml: 'yaml', yml: 'yml', bash: 'sh', shell: 'sh',
  html: 'html', css: 'css', java: 'java', csharp: 'cs', go: 'go', rust: 'rs', php: 'php', markdown: 'md',
};

function textDownload(text: string, base: string, ext: string, mime: string): Promise<void> {
  downloadBlob(new Blob([text], { type: `${mime};charset=utf-8;` }), exportFilename(base, ext));
  return Promise.resolve();
}

function tableColumnsRows(artifact: PaArtifact): { columns: ExportColumn[]; rows: ExportRow[] } | null {
  const c = artifact.content as TableLikeContent | null | undefined;
  if (Array.isArray(c?.columns) && Array.isArray(c?.rows)) {
    const columns = c.columns.filter((col): col is ExportColumn =>
      typeof col === 'object' && col !== null && typeof (col as ExportColumn).key === 'string');
    const rows = c.rows.filter((r): r is ExportRow => typeof r === 'object' && r !== null);
    return { columns, rows };
  }
  // chart: data[] + xKey/yKey → tabella a 2+ colonne derivata dai dati.
  if (Array.isArray(c?.data)) {
    const rows = c.data.filter((r): r is ExportRow => typeof r === 'object' && r !== null);
    const keys = new Set<string>();
    for (const r of rows) Object.keys(r).forEach(k => keys.add(k));
    const columns: ExportColumn[] = [...keys].map(k => ({ key: k, label: k }));
    return { columns, rows };
  }
  return null;
}

export function getArtifactExportOptions(artifact: PaArtifact): ArtifactExportOption[] {
  const base = artifact.slug || artifact.title || 'artefatto';
  switch (artifact.kind) {
    case 'markdown':
      return [{ id: 'md', label: 'Markdown (.md)', run: () => textDownload(bodyOf(artifact), base, 'md', 'text/markdown') }];
    case 'code': {
      const lang = String((artifact.content as { language?: unknown } | null | undefined)?.language ?? '').toLowerCase();
      const ext = CODE_EXT[lang] ?? 'txt';
      return [{ id: 'code', label: `Sorgente (.${ext})`, run: () => textDownload(bodyOf(artifact), base, ext, 'text/plain') }];
    }
    case 'react':
      return [{ id: 'tsx', label: 'Sorgente (.tsx)', run: () => textDownload(bodyOf(artifact), base, 'tsx', 'text/plain') }];
    case 'html':
      return [{ id: 'html', label: 'Pagina (.html)', run: () => textDownload(bodyOf(artifact), base, 'html', 'text/html') }];
    case 'diagram':
      return [{ id: 'mmd', label: 'Mermaid (.mmd)', run: () => textDownload(bodyOf(artifact), base, 'mmd', 'text/plain') }];
    case 'table':
    case 'chart': {
      const tab = tableColumnsRows(artifact);
      if (!tab || tab.columns.length === 0) return [];
      return [
        {
          id: 'csv', label: 'CSV (.csv)',
          run: () => textDownload('\uFEFF' + buildCsv(tab.columns, tab.rows), base, 'csv', 'text/csv'),
        },
        { id: 'xlsx', label: 'Excel (.xlsx)', run: () => exportTableXlsx(tab.columns, tab.rows, base) },
      ];
    }
    case 'timeline':
    case 'kanban':
    case 'mindmap':
    case 'map':
    case 'form':
      return [{
        id: 'json', label: 'Dati (.json)',
        run: () => textDownload(JSON.stringify(artifact.content, null, 2), base, 'json', 'application/json'),
      }];
    // I media hanno il download nel viewer (URL firmato R2); action non esporta.
    case 'file':
    case 'image':
    case 'video':
    case 'audio':
    case 'action':
    default:
      return [];
  }
}
